import type { MessageKey } from '~/i18n/messages.ts';
import type { StaffRole } from '~/lib/session.tsx';

/** The glyphs the rail can draw. Named, with the artwork in NavIcon. */
export type NavIconName =
  | 'dashboard'
  | 'products'
  | 'categories'
  | 'orders'
  | 'coupons'
  | 'customers'
  | 'reviews'
  | 'inquiries'
  | 'content'
  | 'settings';

export interface NavItem {
  to: string;
  label: MessageKey;
  icon: NavIconName;
  /** Omitted means both staff roles see it. */
  requires?: StaffRole;
}

export interface NavGroup {
  /**
   * The cluster's name. Not drawn on the rail, but said in the command bar's trail for the page
   * you are on. The first cluster has none: the front page is not under anything.
   */
  label?: MessageKey;
  items: NavItem[];
}

/**
 * The whole back office, in the order it is worked through.
 *
 * Declared as data rather than markup so the rail, the mobile drawer, the command bar's "where am
 * I" line and the "not built yet" placeholder routes all read from one list — four copies of a
 * menu is four chances for a link to point at a page that no longer exists.
 *
 * Every page is a MAIN link. The rail used to fold pages into four collapsible sections, which
 * bought a shorter list at the price of a click before every navigation and a rail whose shape
 * changed underneath you. Flat costs one screenful and nothing else: nine rows, each with its own
 * glyph, each one click from anywhere.
 *
 * The clusters are what stop nine flat rows reading as one undifferentiated column. They group by
 * subject without gating anything; on the rail they are parted by hairlines rather than named.
 */
export const NAVIGATION: NavGroup[] = [
  {
    items: [{ to: '/', label: 'nav.dashboard', icon: 'dashboard' }],
  },
  {
    label: 'nav.groupCatalogue',
    items: [
      { to: '/products', label: 'nav.products', icon: 'products' },
      { to: '/categories', label: 'nav.categories', icon: 'categories' },
    ],
  },
  {
    label: 'nav.groupSales',
    items: [
      { to: '/orders', label: 'nav.orders', icon: 'orders' },
      { to: '/coupons', label: 'nav.coupons', icon: 'coupons' },
      { to: '/customers', label: 'nav.customers', icon: 'customers' },
      { to: '/reviews', label: 'nav.reviews', icon: 'reviews' },
    ],
  },
  {
    label: 'nav.groupShopfront',
    items: [
      { to: '/content', label: 'nav.content', icon: 'content' },
      { to: '/inquiries', label: 'nav.inquiries', icon: 'inquiries' },
    ],
  },
  {
    label: 'nav.groupSystem',
    items: [
      {
        to: '/settings',
        label: 'nav.settings',
        icon: 'settings',
        // Payment credentials, SMTP passwords and admin accounts live here.
        requires: 'SuperAdmin',
      },
    ],
  },
];

export function visibleGroups(has: (role: StaffRole) => boolean): NavGroup[] {
  return NAVIGATION.map((group) => ({
    ...group,
    items: group.items.filter((item) => !item.requires || has(item.requires)),
  })).filter((group) => group.items.length > 0);
}

/** The page a route lands on — its own row in the rail. */
export function itemFor(pathname: string): NavItem | undefined {
  const all = NAVIGATION.flatMap((group) => group.items);

  // Longest match first, so `/products/new` answers Products rather than the dashboard's `/`.
  return [...all]
    .sort((a, b) => b.to.length - a.to.length)
    .find((item) => (item.to === '/' ? pathname === '/' : pathname.startsWith(item.to)));
}

/**
 * Where a route sits, for the command bar's trail: the cluster it is filed under, if it has one,
 * and its own row in the rail. The dashboard is not under anything, so its trail is itself.
 */
export function trailFor(pathname: string): { group?: MessageKey; item: NavItem } | undefined {
  const item = itemFor(pathname);

  if (!item) {
    return undefined;
  }

  const group = NAVIGATION.find((candidate) => candidate.items.includes(item));

  return { group: group?.label, item };
}
